import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { accountService } from '../_services';
import { toast } from "../_components/Toast";

function ResetPassword(props) {
    const TokenStatus = {
        Validating: 'Validating',
        Valid: 'Valid',
        Invalid: 'Invalid'
    }
    const token = sessionStorage.getItem("jwtToken");
    const tokenStatus = 'Valid'; // Set to Valid since we're in admin context

    function getForm() {
        const initialValues = {
            password: '',
            confirmPassword: ''
        };

        const validationSchema = Yup.object().shape({
            password: Yup.string()
                .min(6, 'Password must be at least 6 characters')
                .required('Password is required'),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], 'Passwords must match')
                .required('Confirm Password is required'),
        });

        function onSubmit({ password, confirmPassword }, { setSubmitting }) {
            toast.clear();
            accountService.resetPassword({ token, password, confirmPassword, user: props.user })
                .then(() => {
                    toast.success('Password reset successful!', { keepAfterRouteChange: true });
                    props.closeModal();
                })
                .catch(error => {
                    setSubmitting(false);
                    toast.error(error);
                });
        }

        return (
            <div className="reset-password-content">
                <div className="user-info-section">
                    <div className="user-avatar">
                        <i className="bi bi-person-circle"></i>
                    </div>
                    <div className="user-details">
                        <h4>{props.user && props.user.firstName} {props.user && props.user.lastName}</h4>
                        <p>{props.user && props.user.email}</p>
                    </div>
                </div>

                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                    {({ errors, touched, isSubmitting }) => (
                        <Form className="password-form">
                            <div className="form-group">
                                <label htmlFor="password">New Password</label>
                                <div className="input-wrapper">
                                    <i className="bi bi-lock input-icon"></i>
                                    <Field 
                                        id="password"
                                        name="password" 
                                        type="password" 
                                        className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                                        placeholder="Enter new password"
                                    />
                                </div>
                                <ErrorMessage name="password" component="div" className="invalid-feedback" />
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <div className="input-wrapper">
                                    <i className="bi bi-lock-fill input-icon"></i>
                                    <Field 
                                        id="confirmPassword"
                                        name="confirmPassword" 
                                        type="password" 
                                        className={`form-control ${errors.confirmPassword && touched.confirmPassword ? 'is-invalid' : ''}`}
                                        placeholder="Confirm new password"
                                    />
                                </div>
                                <ErrorMessage name="confirmPassword" component="div" className="invalid-feedback" />
                            </div>

                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={props.closeModal}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting} 
                                    className="btn btn-primary"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Resetting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check-circle me-2"></i>
                                            Reset Password
                                        </>
                                    )}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>

                <style jsx>{`
                    .reset-password-content {
                        min-width: 450px;
                        max-width: 500px;
                    }

                    .user-info-section {
                        display: flex;
                        align-items: center;
                        gap: 15px;
                        margin-bottom: 15px;
                        padding: 10px;
                        background: #f8f9fa;
                        border-radius: 8px;
                        border-left: 4px solid #007bff;
                    }

                    .user-avatar {
                        font-size: 36px;
                        color: #6c757d;
                    }

                    .user-details h4 {
                        margin: 0;
                        color: #212529;
                        font-weight: 600;
                        font-size: 16px;
                    }

                    .user-details p {
                        margin: 0;
                        color: #6c757d;
                        font-size: 14px;
                    }

                    .password-form {
                        display: flex;
                        flex-direction: column;
                        gap: 10px;
                    }

                    .form-group {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                    }

                    .form-group label {
                        font-weight: 500;
                        color: #495057;
                        font-size: 14px;
                        margin: 0;
                    }

                    .input-wrapper {
                        position: relative;
                        display: flex;
                        align-items: center;
                    }

                    .input-icon {
                        position: absolute;
                        left: 12px;
                        color: #6c757d;
                        font-size: 14px;
                        z-index: 2;
                    }

                    .form-control {
                        width: 100%;
                        padding: 8px 10px 8px 28px;
                        border: 1px solid #ced4da;
                        border-radius: 6px;
                        font-size: 14px;
                        transition: all 0.15s ease-in-out;
                        background-color: #fff;
                        margin: 0;
                    }

                    .form-control:focus {
                        outline: none;
                        border-color: #80bdff;
                        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                    }

                    .form-control.is-invalid {
                        border-color: #dc3545;
                        box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
                    }

                    .invalid-feedback {
                        display: block;
                        width: 100%;
                        margin-top: 2px;
                        font-size: 12px;
                        color: #dc3545;
                    }

                    .form-actions {
                        display: flex;
                        justify-content: flex-end;
                        gap: 10px;
                        margin-top: 10px;
                        padding-top: 10px;
                        border-top: 1px solid #dee2e6;
                    }

                    .btn {
                        display: inline-flex;
                        align-items: center;
                        padding: 8px 16px;
                        border: 1px solid transparent;
                        border-radius: 4px;
                        font-size: 14px;
                        font-weight: 400;
                        line-height: 1.5;
                        text-align: center;
                        text-decoration: none;
                        vertical-align: middle;
                        cursor: pointer;
                        transition: all 0.15s ease-in-out;
                        min-width: 100px;
                        justify-content: center;
                        margin: 0;
                    }

                    .btn-primary {
                        color: #fff;
                        background-color: #007bff;
                        border-color: #007bff;
                    }

                    .btn-primary:hover:not(:disabled) {
                        background-color: #0056b3;
                        border-color: #004085;
                    }

                    .btn-primary:disabled {
                        background-color: #6c757d;
                        border-color: #6c757d;
                        cursor: not-allowed;
                    }

                    .btn-secondary {
                        color: #6c757d;
                        background-color: #fff;
                        border-color: #6c757d;
                    }

                    .btn-secondary:hover:not(:disabled) {
                        color: #fff;
                        background-color: #6c757d;
                        border-color: #6c757d;
                    }

                    .btn-secondary:disabled {
                        opacity: 0.65;
                        cursor: not-allowed;
                    }

                    .spinner-border-sm {
                        width: 1rem;
                        height: 1rem;
                        border-width: 0.15em;
                    }

                    .spinner-border {
                        display: inline-block;
                        vertical-align: text-bottom;
                        border: 0.25em solid currentColor;
                        border-right-color: transparent;
                        border-radius: 50%;
                        animation: spinner-border 0.75s linear infinite;
                    }

                    @keyframes spinner-border {
                        to {
                            transform: rotate(360deg);
                        }
                    }

                    .me-2 {
                        margin-right: 0.5rem;
                    }

                    @media (max-width: 576px) {
                        .reset-password-content {
                            min-width: 300px;
                            max-width: 100%;
                        }

                        .form-actions {
                            flex-direction: column;
                        }

                        .btn {
                            width: 100%;
                        }

                        .user-info-section {
                            padding: 8px;
                        }
                    }
                `}</style>
            </div>
        );
    }

    function getBody() {
        switch (tokenStatus) {
            case TokenStatus.Valid:
                return getForm();
            case TokenStatus.Invalid:
                return (
                    <div className="error-state">
                        <div className="error-icon">
                            <i className="bi bi-exclamation-triangle"></i>
                        </div>
                        <p>Token validation failed. If the token has expired, you can get a new one at the <Link to="forgot-password">forgot password</Link> page.</p>
                        <style jsx>{`
                            .error-state {
                                text-align: center;
                                padding: 40px 20px;
                                min-width: 400px;
                            }

                            .error-icon {
                                color: #dc3545;
                                font-size: 48px;
                                margin-bottom: 16px;
                            }

                            .error-state p {
                                color: #6c757d;
                                margin: 0;
                            }
                        `}</style>
                    </div>
                );
            case TokenStatus.Validating:
                return (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Validating token...</p>
                        <style jsx>{`
                            .loading-state {
                                text-align: center;
                                padding: 40px 20px;
                                min-width: 400px;
                            }

                            .loading-spinner {
                                width: 32px;
                                height: 32px;
                                border: 3px solid #e9ecef;
                                border-top: 3px solid #007bff;
                                border-radius: 50%;
                                animation: spin 1s linear infinite;
                                margin: 0 auto 16px;
                            }

                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }

                            .loading-state p {
                                color: #6c757d;
                                margin: 0;
                            }
                        `}</style>
                    </div>
                );
        }
    }

    return getBody();
}

export { ResetPassword };